import { createContext, useContext, useEffect, useState, ReactNode } from 'react';
import { useAuth } from './AuthContext';
import { supabase } from '@/integrations/supabase/client';
import { db } from '@/lib/supabaseQuery';
import { capitalizeWords } from '@/lib/utils';
import { logger } from '@/utils/logger';

interface AvatarContextType {
  avatarUrl: string;
  displayName: string;
  loading: boolean;
  refreshAvatar: () => Promise<void>;
}

interface UserProfilePayload {
  image?: string | null;
  display_name?: string | null;
  name?: string | null;
  lastname?: string | null;
}

const AvatarContext = createContext<AvatarContextType | undefined>(undefined);

export const useAvatar = () => {
  const context = useContext(AvatarContext);
  if (!context) {
    throw new Error('useAvatar must be used within AvatarProvider');
  }
  return context;
};

export const AvatarProvider = ({ children }: { children: ReactNode }) => {
  const { user } = useAuth();
  const [avatarUrl, setAvatarUrl] = useState<string>("");
  const [displayName, setDisplayName] = useState<string>("");
  const [loading, setLoading] = useState(true);

  const fetchUserData = async () => {
    if (!user) {
      setLoading(false);
      return;
    }

    setLoading(true);
    try {
      // Use db.fetchOne with user.id (most reliable) - follows centralized query pattern
      let data = await db.fetchOne<{
        image: string | null;
        display_name: string | null;
        name: string | null;
        lastname: string | null;
      }>('users', 'image, display_name, name, lastname', [
        { column: 'id', operator: 'eq', value: user.id }
      ]);


      if (data) {
        logger.debug('Fetched avatar data:', data);
        setAvatarUrl(data.image || "");
        const rawName = data.display_name || 
          [data.name, data.lastname].filter(Boolean).join(' ') || 
          // Fallback to auth metadata if DB has no name
          user.user_metadata?.full_name ||
          user.user_metadata?.name ||
          "";
        setDisplayName(capitalizeWords(rawName));
      } else {
        // No DB record - use auth metadata as fallback
        const fallbackName = user.user_metadata?.full_name || 
          user.user_metadata?.name || 
          "";
        setDisplayName(capitalizeWords(fallbackName));
        setAvatarUrl("");
      }
    } catch (error) {
      logger.error('Error fetching user data:', error);
      // On error, try auth metadata fallback
      const fallbackName = user.user_metadata?.full_name || 
        user.user_metadata?.name || 
        "";
      setDisplayName(capitalizeWords(fallbackName));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (user) {
      fetchUserData();
      
      // Set up real-time subscription for avatar updates using user.id
      logger.debug('Setting up real-time subscription for user:', user.id);
      const channel = supabase
        .channel(`user-profile-changes-${Math.random().toString(36).slice(2)}`)
        .on(
          'postgres_changes',
          {
            event: 'UPDATE',
            schema: 'public',
            table: 'users',
            filter: `id=eq.${user.id}`
          },
          (payload) => {
            logger.debug('Real-time update received:', payload);
            if (payload.new) {
              const newData = payload.new as UserProfilePayload;
              setAvatarUrl(newData.image || "");
              const rawName = newData.display_name || 
                [newData.name, newData.lastname].filter(Boolean).join(' ') || 
                "";
              setDisplayName(capitalizeWords(rawName));
            }
          }
        )
        .subscribe((status) => {
          logger.debug('Real-time subscription status:', status);
        });

      return () => {
        logger.debug('Cleaning up real-time subscription');
        supabase.removeChannel(channel);
      };
    } else {
      setAvatarUrl("");
      setDisplayName("");
      setLoading(false);
    }
  }, [user]);

  return (
    <AvatarContext.Provider value={{ avatarUrl, displayName, loading, refreshAvatar: fetchUserData }}>
      {children}
    </AvatarContext.Provider>
  );
};
